import React, { useState, useEffect } from 'react';
import { BubbleMenu } from '@tiptap/react/menus';
import Icon from '../AppIcon';

const BubbleButton = ({ onClick, title, children, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-1.5 rounded transition-colors hover:bg-gray-100 text-gray-600 ${className}`}
  >
    {children}
  </button>
);

function getFaviconUrl(url) {
  try {
    const host = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${host}&sz=32`;
  } catch {
    return null;
  }
}

export default function LinkBubbleMenu({ editor, onOpenEdit }) {
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const href = editor?.getAttributes('link')?.href || '';

  useEffect(() => {
    if (!href || !href.startsWith('http')) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreview(null);
    fetch(`https://linkmeta.dev/api/v1/extract?url=${encodeURIComponent(href)}`)
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        if (json?.status === 'success' && json?.data) {
          setPreview({
            title: json.data.title || json.data.siteName || new URL(href).hostname,
            description: json.data.description || '',
            favicon: json.data.favicon || getFaviconUrl(href),
            url: json.data.url || href,
          });
        } else {
          setPreview({
            title: new URL(href).hostname,
            description: '',
            favicon: getFaviconUrl(href),
            url: href,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          try {
            setPreview({
              title: new URL(href).hostname,
              description: '',
              favicon: getFaviconUrl(href),
              url: href,
            });
          } catch {
            setPreview(null);
          }
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [href]);

  const handleCopy = async () => {
    if (href) {
      await navigator.clipboard.writeText(href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleEdit = () => {
    editor?.chain().focus().extendMarkRange('link').run();
    onOpenEdit?.();
  };

  const handleRemove = () => {
    editor?.chain().focus().extendMarkRange('link').unsetLink().run();
  };

  if (!editor) return null;

  const displayUrl = preview?.url || href;
  const fullShort = displayUrl.replace(/^https?:\/\/(www\.)?/, '');
  const shortUrl = fullShort.slice(0, 35);
  const truncatedUrl = shortUrl.length < fullShort.length ? shortUrl + '...' : shortUrl;

  return (
    <BubbleMenu
      editor={editor}
      pluginKey="linkBubbleMenu"
      shouldShow={({ editor: ed }) => ed.isActive('link')}
      tippyOptions={{
        placement: 'bottom-start',
        maxWidth: 420,
      }}
      className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-xl shadow-lg min-w-[320px] max-w-[420px]"
      dir="rtl"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-3">
          {previewLoading ? (
            <div className="w-8 h-8 rounded bg-gray-100 animate-pulse shrink-0" />
          ) : (preview?.favicon || getFaviconUrl(href)) ? (
            <img
              src={preview?.favicon || getFaviconUrl(href)}
              alt=""
              className="w-8 h-8 rounded shrink-0 object-contain"
            />
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-gray-900 truncate" dir="ltr" style={{ fontSize: '14px' }}>
              {previewLoading ? '...' : (preview?.title || truncatedUrl)}
            </div>
            <div className="text-gray-500 text-sm truncate mt-0.5" dir="ltr">
              {truncatedUrl}
            </div>
            {preview?.description && (
              <div className="text-gray-600 text-xs mt-1.5 line-clamp-2" style={{ fontFamily: 'Amiri, serif' }}>
                {preview.description}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0 border-s border-gray-200 ps-2">
        <BubbleButton onClick={handleCopy} title={copied ? 'تم النسخ' : 'نسخ الرابط'}>
          <Icon name={copied ? 'Check' : 'Copy'} size={16} />
        </BubbleButton>
        <BubbleButton onClick={handleEdit} title="تعديل الرابط">
          <Icon name="Pencil" size={16} />
        </BubbleButton>
        <BubbleButton onClick={handleRemove} title="إزالة الرابط" className="hover:!bg-red-50 hover:!text-red-600">
          <Icon name="Unlink" size={16} />
        </BubbleButton>
      </div>
    </BubbleMenu>
  );
}
